import {CartProvider as ShopifyCartProvider} from '@shopify/hydrogen';
import {CountryCode} from '@shopify/hydrogen/dist/esnext/storefront-api-types';

import CartUIProvider from './CartUIProvider.client';

/**
 * A client component that creates a cart object and provides callbacks that can be accessed by any descendent component using the `useCart` hook and related hooks
 */
export default function CartProvider({
  children,
  numCartLines,
  countryCode,
}: {
  children: React.ReactNode;
  numCartLines?: number;
  countryCode?: string;
}) {
  return (
    <CartUIProvider>
      <Provider numCartLines={numCartLines} countryCode={countryCode}>
        {children}
      </Provider>
    </CartUIProvider>
  );
}

function Provider({
  children,
  numCartLines,
  countryCode,
}: {
  children: React.ReactNode;
  numCartLines?: number;
  countryCode?: string;
}) {
  const cartFragment = `
  fragment CartFragment on Cart {
    id
    checkoutUrl
    totalQuantity
    buyerIdentity {
      countryCode
      email
      phone
    }
    lines(first: $numCartLines) {
      edges {
        node {
          id
          quantity
          attributes {
            key
            value
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            compareAtAmountPerQuantity {
              amount
              currencyCode
            }
          }
          merchandise {
            ... on ProductVariant {
              id
              availableForSale
              compareAtPriceV2 {
                ...MoneyFragment
              }
              priceV2 {
                ...MoneyFragment
              }
              requiresShipping
              title
              image {
                ...ImageFragment
              }
              product {
                handle
                title
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
    cost {
      subtotalAmount {
        ...MoneyFragment
      }
      totalAmount {
        ...MoneyFragment
      }
      totalDutyAmount {
        ...MoneyFragment
      }
      totalTaxAmount {
        ...MoneyFragment
      }
    }
    note
    attributes {
      key
      value
    }
    discountCodes {
      code
    }
  }

  fragment MoneyFragment on MoneyV2 {
    currencyCode
    amount
  }
  fragment ImageFragment on Image {
    id
    url
    altText
    width
    height
  }
  `;

  return (
    <>
      <ShopifyCartProvider
        numCartLines={numCartLines}
        cartFragment={cartFragment}
        countryCode={countryCode as CountryCode | undefined}
      >
        {children}
      </ShopifyCartProvider>
    </>
  );
}
